import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import FeedList from "@/components/FeedList";
import UserAvatar from "@/components/UserAvatar";
import api, { unwrap, apiError } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import { toast } from "sonner";
import {
  Plus,
  Check,
  TrendingUp,
  Compass,
  Search as SearchIcon,
} from "lucide-react";

/**
 * Discover — 1:1 port of iOS `src/app/(app)/(tabs)/discover.tsx`.
 *
 * Layout (top → bottom):
 *   • Cursive "Discover" (Grand Hotel 32px)  +  one-line subtitle
 *   • Tap-through search bar → routes to /search
 *   • Explore / Trending glass segmented control (rainbow-border active pill)
 *   • Trending mode: "PEOPLE RISING" horizontal strip pinned on top of the feed
 *   • FeedList below
 */

const RAINBOW =
  "linear-gradient(90deg, #FFD600, #FF7A00, #FF0069, #D300C5, #7638FA)";

/* -----------------------------------------------------------------------
 * Reusable glass segmented control — matches iOS `GlassSurface` segments.
 * Two options, rainbow-border active pill, active label bold.
 * ----------------------------------------------------------------------- */
export function SegmentedTabs({
  value,
  onChange,
  options, // [{ key, label, Icon }]
  testId = "segmented-tabs",
}) {
  return (
    <div
      className="w-full rounded-[13px] border border-border bg-card/80 backdrop-blur-md p-[2.5px] overflow-hidden"
      data-testid={testId}
    >
      <div className="flex w-full">
        {options.map(({ key, label, Icon }) => {
          const active = value === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onChange(key)}
              className={`relative flex-1 h-[34px] rounded-[10.5px] flex items-center justify-center gap-1.5 transition-opacity ${
                active ? "" : "opacity-70 hover:opacity-90 active:opacity-60"
              }`}
              data-testid={`${testId}-${key}`}
            >
              {active && (
                // Rainbow-gradient outline pill with an ELEVATED white
                // inner fill (matches iOS `theme.elevated` — the card
                // color, NOT the gray `secondaryBg`).
                <span
                  className="absolute inset-0 rounded-[10.5px]"
                  style={{ background: RAINBOW, padding: "1.25px" }}
                >
                  <span className="block w-full h-full rounded-[9.25px] bg-card" />
                </span>
              )}
              <span className="relative flex items-center gap-1.5">
                <Icon
                  size={15}
                  strokeWidth={1.9}
                  className={active ? "text-foreground" : "text-muted-foreground"}
                />
                <span
                  className={`text-[14px] leading-[18px] ${
                    active ? "text-foreground font-semibold" : "text-muted-foreground"
                  }`}
                >
                  {label}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------------
 * Search bar entry — tap to route to full search screen.
 * ----------------------------------------------------------------------- */
export function DiscoverSearchBar({ testId = "discover-search" }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate("/search")}
      className="w-full flex items-center gap-2 h-[38px] px-3 rounded-[11px] bg-secondary/70 hover:bg-secondary transition-colors mt-3"
      data-testid={testId}
    >
      <SearchIcon size={16} strokeWidth={1.9} className="text-muted-foreground" />
      <span className="text-muted-foreground text-[15px]">
        Search people, posts, tags
      </span>
    </button>
  );
}

/* -----------------------------------------------------------------------
 * "PEOPLE RISING" horizontal strip — small, cursive-named cards with a
 * rainbow-filled "Follow" pill.  Follow/unfollow are optimistic.
 * ----------------------------------------------------------------------- */
function PeopleRisingStrip() {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followed, setFollowed] = useState(new Set());
  const [unfollowed, setUnfollowed] = useState(new Set());

  useEffect(() => {
    (async () => {
      try {
        let list = [];
        try {
          const res = await api.get(EP.followRecommendations(1, 8));
          const d = unwrap(res.data);
          list = d?.items || d?.users || (Array.isArray(d) ? d : []);
        } catch {
          /* fall through */
        }
        if (list.length === 0) {
          const res2 = await api.get(EP.suggestedUsers(1, 8));
          const d2 = unwrap(res2.data);
          list = d2?.items || d2?.users || (Array.isArray(d2) ? d2 : []);
        }
        const seen = new Set();
        const unique = list.filter((u) => {
          const k = String(u?.user_id ?? u?.id ?? u?.username ?? "");
          if (!k || seen.has(k)) return false;
          seen.add(k);
          return true;
        });
        setPeople(unique.slice(0, 8));
      } catch {
        /* silent */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const follow = async (u) => {
    const id = u.user_id ?? u.id;
    const username = u.username;
    setFollowed((s) => new Set(s).add(id));
    setUnfollowed((s) => {
      const n = new Set(s);
      n.delete(id);
      return n;
    });
    try {
      // iOS spec: prefer POST /follow_by_username { user_id, username };
      // fall back to POST /follow/{id} when only the id is known.
      if (username) {
        await api.post(EP.followByUsername, { user_id: id, username });
      } else {
        await api.post(EP.followUser(id), {});
      }
      toast.success("Followed");
    } catch (e) {
      setFollowed((s) => {
        const n = new Set(s);
        n.delete(id);
        return n;
      });
      toast.error(apiError(e));
    }
  };

  const unfollow = async (u) => {
    const id = u.user_id ?? u.id;
    setUnfollowed((s) => new Set(s).add(id));
    setFollowed((s) => {
      const n = new Set(s);
      n.delete(id);
      return n;
    });
    try {
      // iOS spec: DELETE /users/{id}/follow.
      await api.delete(EP.unfollowUser(id));
    } catch (e) {
      setUnfollowed((s) => {
        const n = new Set(s);
        n.delete(id);
        return n;
      });
      setFollowed((s) => new Set(s).add(id));
      toast.error(apiError(e));
    }
  };

  if (loading || people.length === 0) return null;

  return (
    <div className="mt-4 mb-2" data-testid="trending-people-strip">
      <div className="flex items-center gap-1.5 mb-3 px-1">
        <TrendingUp size={13} strokeWidth={1.8} className="text-muted-foreground" />
        <span
          className="text-muted-foreground text-[11px] font-bold uppercase"
          style={{ letterSpacing: "0.5px" }}
        >
          People Rising
        </span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
        {people.map((u) => {
          const id = u.user_id ?? u.id;
          const isFollowed = !unfollowed.has(id) && (followed.has(id) || u.viewer_is_following);
          return (
            <div
              key={String(id ?? u.username)}
              className="shrink-0 w-[152px] rounded-2xl border border-border bg-card p-4 flex flex-col items-center"
              data-testid={`trending-people-card-${id}`}
            >
              <Link to={`/${u.username}`}>
                <UserAvatar user={u} size="lg" />
              </Link>
              <Link
                to={`/${u.username}`}
                className="mt-2 text-foreground font-grand-hotel text-[19px] leading-6 truncate max-w-full"
                title={u.display_name || u.username}
              >
                {u.display_name || u.username}
              </Link>
              <div className="text-muted-foreground text-[13px] truncate max-w-full">
                @{u.username}
              </div>
              <button
                type="button"
                onClick={() => (isFollowed ? unfollow(u) : follow(u))}
                className="mt-3 h-8 px-3.5 rounded-xl flex items-center gap-1 text-[13px] font-semibold transition-all active:opacity-70"
                style={
                  isFollowed
                    ? { backgroundColor: "hsl(var(--secondary))", color: "hsl(var(--foreground))" }
                    : { background: RAINBOW, color: "#fff" }
                }
                data-testid={`trending-follow-${id}`}
              >
                {isFollowed ? (
                  <>
                    <Check size={14} strokeWidth={2} />
                    Following
                  </>
                ) : (
                  <>
                    <Plus size={14} strokeWidth={2} />
                    Follow
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------------
 * Cursive display header — used by Discover and Opinions.
 * ----------------------------------------------------------------------- */
export function CursiveHeader({ title, subtitle, right }) {
  return (
    <div className="px-4 pt-2">
      <div className="flex items-center gap-2.5">
        <h1
          className="font-grand-hotel text-foreground"
          style={{ fontSize: 32, lineHeight: "42px" }}
        >
          {title}
        </h1>
        {right}
      </div>
      {subtitle && (
        <p
          className="text-muted-foreground"
          style={{ fontSize: 13, lineHeight: "17px", marginTop: 1 }}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------- */
export default function Discover() {
  const [tab, setTab] = useState("explore");

  const header = (
    <>
      <CursiveHeader
        title="Discover"
        subtitle="Explore new posts and see what's trending."
      />
      <div className="px-4">
        <DiscoverSearchBar />
        <div className="mt-3 mb-1">
          <SegmentedTabs
            value={tab}
            onChange={setTab}
            options={[
              { key: "explore", label: "Explore", Icon: Compass },
              { key: "trending", label: "Trending", Icon: TrendingUp },
            ]}
            testId="discover-tabs"
          />
        </div>
        {tab === "trending" && <PeopleRisingStrip />}
      </div>
    </>
  );

  return (
    <div className="w-full" data-testid="discover-screen">
      {tab === "explore" ? (
        <FeedList
          key="explore"
          endpointFn={EP.feedDiscoverStandard}
          testid="feed-discover"
          header={header}
          fallbackToOwnPosts={false}
          emptyState={
            <div className="px-6 py-16 flex flex-col items-center text-center gap-3">
              <Compass size={28} className="text-muted-foreground" />
              <h3 className="text-foreground text-[17px] font-bold">
                Nothing to explore yet.
              </h3>
              <p className="text-muted-foreground text-[14px]">
                Fresh posts will appear here soon.
              </p>
            </div>
          }
        />
      ) : (
        <FeedList
          key="trending"
          endpointFn={EP.feedTrendingStandard}
          testid="feed-trending"
          header={header}
          fallbackToOwnPosts={false}
          emptyState={
            <div className="px-6 py-16 flex flex-col items-center text-center gap-3">
              <TrendingUp size={28} className="text-muted-foreground" />
              <h3 className="text-foreground text-[17px] font-bold">
                Nothing is trending right now.
              </h3>
              <p className="text-muted-foreground text-[14px]">
                Check back soon as the community reacts.
              </p>
            </div>
          }
        />
      )}
    </div>
  );
}
